import { useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, CaretDownIcon, CaretRightIcon, CopyIcon, TrashIcon } from '@phosphor-icons/react';
import type { ComponentType } from 'react';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/textarea';
import { cn } from '~/lib/utils';
import { slideTitle, type OriginalSlide, type SlideRow } from './artifact-edit-draft';
import { STRIP_ACTIONS, STRIP_ACTION_LABEL, stripActionReason, type StripAction } from './canvas-slide-strip-gate';

/** Mirrors the server's MANUAL_NOTES_MAX_CHARS (src/artifacts/manual-edit.ts). */
const NOTES_MAX = 10_000;

const ICON: Record<StripAction, ComponentType<{ size?: number }>> = {
  moveUp: ArrowUpIcon,
  moveDown: ArrowDownIcon,
  duplicate: CopyIcon,
  delete: TrashIcon,
};

export interface CanvasSlideStripProps {
  rows: readonly SlideRow[];
  originals: readonly OriginalSlide[];
  selected: number;
  onSelect: (index: number) => void;
  onAction: (action: StripAction, index: number) => void;
  onNotes: (index: number, notes: string) => void;
  /** The server's slide ceiling (from the deck's read); undefined until known. */
  maxSlides?: number;
  /** Why the arrangement is locked right now (a save is running, or the draft is blocked); null when it is not. Also locks the notes box. */
  frozenReason?: string | null;
}

/**
 * The deck's slide list in edit mode. Layout choice: ONE collapsible block above
 * the frame, its list capped at 10rem and scrolling inside itself, with the
 * selected slide's speaker notes under it. A vertical list needs no horizontal
 * room, so it fits the panel at its narrowest width and in the phone switch view;
 * collapsing it hands the whole panel back to the frame.
 */
export function CanvasSlideStrip({ rows, originals, selected, onSelect, onAction, onNotes, maxSlides, frozenReason = null }: CanvasSlideStripProps) {
  const [open, setOpen] = useState(true);
  const current = rows[selected] ?? null;
  return (
    <section data-testid="canvas-slide-strip" aria-label="Slides" className="shrink-0 border-b border-border bg-card">
      <button
        type="button"
        data-testid="canvas-slide-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-xs font-medium text-foreground"
      >
        {open ? <CaretDownIcon size={12} aria-hidden /> : <CaretRightIcon size={12} aria-hidden />}
        Slides ({rows.length})
      </button>
      {open && (
        <div className="px-3 pb-3">
          <ol className="max-h-40 space-y-1 overflow-y-auto" aria-label="Slide order">
            {rows.map((row, i) => {
              const title = slideTitle(originals[row.from]?.html ?? '', row.from + 1);
              const active = i === selected;
              return (
                <li
                  key={`${row.from}:${row.copy ? 'copy' : 'orig'}:${i}`}
                  data-testid={`canvas-slide-row-${i}`}
                  className={cn('flex items-center gap-1 rounded-md border border-transparent', active && 'border-border bg-muted')}
                >
                  <button
                    type="button"
                    data-testid={`canvas-slide-select-${i}`}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => onSelect(i)}
                    className="flex min-w-0 flex-1 items-baseline gap-2 rounded-md px-2 py-1 text-left text-sm"
                  >
                    <span className="shrink-0 text-xs text-muted-foreground">{i + 1}</span>
                    <span className="min-w-0 truncate">{title}</span>
                    {row.copy && <span className="shrink-0 text-xs text-muted-foreground">(copy)</span>}
                  </button>
                  {STRIP_ACTIONS.map((action) => {
                    const reason = stripActionReason(action, i, rows.length, maxSlides, frozenReason);
                    const Icon = ICON[action];
                    return (
                      <Button
                        key={action}
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        data-testid={`canvas-slide-${action}-${i}`}
                        aria-label={STRIP_ACTION_LABEL[action]}
                        tooltip={reason ?? STRIP_ACTION_LABEL[action]}
                        disabled={reason !== null}
                        onClick={() => onAction(action, i)}
                      >
                        <Icon size={14} />
                      </Button>
                    );
                  })}
                </li>
              );
            })}
          </ol>
          {current && (
            <label className="mt-2 block text-xs text-muted-foreground">
              Speaker notes
              <Textarea
                data-testid="canvas-slide-notes"
                aria-label="Speaker notes"
                value={current.notes}
                maxLength={NOTES_MAX}
                disabled={frozenReason !== null}
                onChange={(e) => onNotes(selected, e.target.value)}
                className="mt-1 min-h-16 max-h-32 text-sm"
              />
            </label>
          )}
        </div>
      )}
    </section>
  );
}
